import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import { CURRENT_LANGUAGE, ResponseCode, SENSITIVE_URL } from 'constants/global';
import { message, Modal } from 'antd';
import i18next from 'i18next';
import mem from 'mem';
import { TrackLogActionEnum } from 'constants/logger';
import { pick } from 'lodash';
import { getUrlParams } from './url';
import { catchRequestError } from './sentry';
import { customSessionStorage } from './storage';
import { trackToLogger } from './logger';
import { safeStringify } from './string';

function getCodeMessage() {
  const codeMessage: { [key: number]: string } = {
    200: i18next.t('lang461'),
    201: i18next.t('lang462'),
    202: i18next.t('lang463'),
    204: i18next.t('lang464'),
    400: i18next.t('lang465'),
    401: i18next.t('lang466'),
    403: i18next.t('lang467'),
    404: i18next.t('lang468'),
    406: i18next.t('lang469'),
    410: i18next.t('lang470'),
    422: i18next.t('lang471'),
    500: i18next.t('lang472'),
    502: i18next.t('lang473'),
    503: i18next.t('lang474'),
    504: i18next.t('lang475'),
  };
  return codeMessage;
}

const CancelSource = axios.CancelToken.source;

export const isRequestCancel = axios.isCancel;

export const isRequestCancelThrow = (error: Error) => {
  return error.message === '__THROW__';
};

// 请求的URL
const requestingUrls = {};

let showRefreshModal = false;

export interface IRequestConfig extends AxiosRequestConfig {
  notAllowCancel: boolean;
  cancelKey: string;
  cancelThrow: boolean;
  complete: boolean;
  ignoreErrorMsg: boolean;
  /** logger 上报用到 */
  startTimestamp?: number;
  /** 不对请求结果进行处理 */
  notFormat: boolean;
}

// 当前请求的唯一标识，当请求链接和参数都相同时会阻止重复请求
function generateRequestKey(config: IRequestConfig) {
  if (config.cancelKey) {
    return config.cancelKey;
  }
  return `${config.url}&${getParamsStr(config.data || config.params)}`;
}

// config.data在request中是对象，在response中是字符串
function getParamsStr(params) {
  if (!params) {
    return '';
  }
  if (typeof params === 'string') {
    return params;
  }
  if (typeof params === 'object') {
    return JSON.stringify(params);
  }
  return '';
}

// cancel request 拦截
axios.interceptors.request.use((config: IRequestConfig) => {
  // 记录请求发起时刻
  // eslint-disable-next-line no-param-reassign
  config.startTimestamp = Date.now();
  if (config.notAllowCancel) {
    return config;
  }
  const requestKey = generateRequestKey(config);
  if (requestKey in requestingUrls) {
    console.warn('请求被取消了，如果不想被取消，请设置[notAllowCancel: true]');
    // 这里会吃掉请求，容易产生bug
    // eslint-disable-next-line no-promise-executor-return
    return new Promise((res) => null);
  }
  requestingUrls[requestKey] = true;
  return config;
});

// cancel response 拦截，单独写以保证可读取到error.config
axios.interceptors.response.use(
  (response: AxiosResponse & { config: IRequestConfig }) => {
    if (response.config.notAllowCancel) {
      return response;
    }
    const requestKey = generateRequestKey(response.config);

    // 用户在狂点按钮发送请求的情况下，还是有几率可以在按钮状态变更后到按钮消失前这段时间发送请求，所以这里稍作延迟再移除请求标识
    setTimeout(() => {
      delete requestingUrls[requestKey];
    }, 300);
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      if (error.message === '__CANCEL__') {
        throw error;
      } else {
        return new Promise(() => {});
      }
    } else {
      const requestKey = generateRequestKey(error.config);
      delete requestingUrls[requestKey];
      throw error;
    }
  }
);

// 初步数据处理
axios.interceptors.response.use(
  (response: AxiosResponse & { config: IRequestConfig }) => {
    const { retmsg, retcode, result } = response.data;
    const { method, url, data, startTimestamp } = response.config;
    const endTimestamp = Date.now();

    const logContent = {
      url,
      method: method?.toUpperCase(),
      status: response.status,
      dataCode: retcode,
      startTimestamp: startTimestamp ?? endTimestamp,
      endTimestamp,
    };

    if (response.config.notFormat) {
      trackToLogger({
        logAction: TrackLogActionEnum.RES,
        logContent: {
          ...logContent,
          // 当失败时才把请求参数上报上去
          reqData: response.status === 200 ? undefined : data,
          // 当失败时才把响应体上报上去
          resData: response.status === 200 ? undefined : safeStringify(response.data),
        },
      });
      return response;
    }
    trackToLogger({
      logAction: TrackLogActionEnum.RES,
      logContent: {
        ...logContent,
        // 当失败时才把请求参数上报上去
        reqData:
          response.status === 200 && retcode === ResponseCode.SUCCESS && !SENSITIVE_URL.includes(url!)
            ? undefined
            : data,
        // 当失败时才把响应体上报上去
        resData: response.status === 200 && retcode === ResponseCode.SUCCESS ? undefined : safeStringify(response.data),
      },
    });

    // return complete data
    if (response.config.complete) {
      return {
        ...response,
        data: {
          code: response.data.retcode,
          message: response.data.retmsg,
          result: response.data.result,
        },
      };
    }
    if (retcode === ResponseCode.SUCCESS) {
      return result;
    }

    if (!response?.config?.ignoreErrorMsg) {
      message.error(retmsg);
    }

    catchRequestError(`接口地址: ${url}，业务[code]:${retcode}`, {
      method,
      url,
      code: retcode,
      data: SENSITIVE_URL.includes(url!) ? undefined : response?.config?.data,
      resData: response?.data,
      startTimestamp,
    });
    return Promise.reject();
  },
  (err) => {
    console.log(err);
    if (err?.message === '__CANCEL__') {
      return Promise.reject();
    }

    const status = err?.response?.status;
    const { data, method, url, startTimestamp } = err?.config || {};

    // 打点上报
    trackToLogger({
      logAction: TrackLogActionEnum.RES,
      logContent: {
        url,
        method: method?.toUpperCase(),
        // 为空时表示网络错误，用特殊的数字区分
        status: status ?? 77777,
        // 为空时表示网络错误，用特殊的数字区分
        dataCode: err?.response?.data?.retcode ?? 77777,
        startTimestamp: startTimestamp || Date.now(),
        endTimestamp: Date.now(),
        reqData: SENSITIVE_URL.includes(url) ? undefined : data,
        resData: err?.response?.data,
      },
    });

    if (status === 401) {
      customSessionStorage.setItem('loginReason', 'http401');
      // 需要跳转的三方授权路径
      const oauthLinks = ['bigoauth/appsflyer', 'oauth/authorize', 'bigoauth/oauth2'];
      if (oauthLinks.some((el) => window.location.href.includes(el))) {
        window.location.href = `/login?from=${encodeURIComponent(window.location.href)}`;
      } else {
        window.location.href = '/login';
      }
      return Promise.reject(err);
    }

    // 登录用户变更了
    if (status === 403 && err?.response?.data?.retcode === 300001 && !showRefreshModal) {
      // 增加该标识以防止每个请求各出现一个弹窗
      showRefreshModal = true;
      Modal.confirm({
        content: i18next.t('lang460'),
        okText: i18next.t('lang350'),
        cancelText: i18next.t('lang090'),
        centered: true,
        onOk() {
          showRefreshModal = false;
          window.location.reload();
        },
        onCancel() {
          showRefreshModal = false;
        },
      });
      return Promise.reject(err);
    }

    if (axios.isCancel(err)) {
      throw err;
    }

    // http请求错误上报sentry
    if (status && status >= 400 && status !== 401) {
      catchRequestError(`http请求失败，status: ${status}；接口地址: [${url}]`, {
        ...pick(err?.config, ['method', 'url', 'startTimestamp']),
        data: SENSITIVE_URL.includes(url) ? undefined : err?.config?.data,
        resData: err?.response?.data,
        error: err,
      });
    }

    // return complete data
    if (err?.config?.complete) {
      throw err?.response || err;
    }

    const codeMessage = getCodeMessage();
    if (err?.response?.status in codeMessage) {
      return Promise.reject(new Error(codeMessage[err?.response?.status]));
    }

    return Promise.reject(err);
  }
);

/* 请求方法 */
export function getRequest<T>(url: string, params = {}, config = {}): Promise<T> {
  return axios.get<T>(url, {
    params,
    ...config,
  }) as unknown as Promise<T>;
}

export function postRequest<T>(url: string, params = {}, config = {}): Promise<T> {
  return axios
    .post<T>(url, params, {
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': CURRENT_LANGUAGE,
        'bigo-org-id': getUrlParams('orgId') || '',
        'bigo-ad-account-id': getUrlParams('accountId') || '',
        // @ts-ignore
        'bigo-ads-uid': window.DSP_USER_ID || config?.uid || '',
        'bigo-origin-domain': window?.location?.hostname || '',
      },
      ...config,
    })
    .catch((e) => {
      console.log(e);
    }) as unknown as Promise<T>;
}

export function formDataRequest<T>(url: string, params = {}, config = {}): Promise<T> {
  return axios
    .post<T>(url, params, {
      headers: {
        'Content-Type': 'multipart/form-data;charset=utf-8',
        'Accept-Language': CURRENT_LANGUAGE,
        'bigo-org-id': getUrlParams('orgId') || '',
        'bigo-ad-account-id': getUrlParams('accountId') || '',
        // @ts-ignore
        'bigo-ads-uid': window.DSP_USER_ID || '',
      },
      ...config,
    })
    .catch((e) => {
      console.log(e);
    }) as unknown as Promise<T>;
}

export function putRequest(url: string, params = {}) {
  return axios.post(url, params, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

// cache
const PROMISE_STORE = new Map(); // 共用一个 store

// 删除缓存
function deleteCache(k: string) {
  PROMISE_STORE.delete(k);
}

// mem 的 maxAge 管理只有在同一个 mem 中才生效，而这里每次请求都会新增一个 mem，所以过期管理需要自定义
function checkMaxAge(k: string) {
  const item = PROMISE_STORE.get(k);
  const delay = (item?.maxAge || 0) - Date.now();
  if (delay <= 0) {
    deleteCache(k);
  }
}

/** 缓存的配置信息 */
export interface MemConfig {
  /** 过期时长 */
  maxAge?: MaxAgeEnum;
  /** 缓存的key获取的方法，默认使用url和入参 */
  cacheKey?: (params?: any[]) => string;
  /** 强制刷新请求，默认否 */
  forceUpdateCache?: boolean;
  /** 是否使用缓存，默认否 */
  useCache?: boolean;
}

/** 建议的缓存时长 */
export enum MaxAgeEnum {
  /** 列表、表单提交等，1s */
  FAST = 1000,
  /** 默认值，需要缓存的其他情况，60s */
  NORMAL = 60000,
  /** 数据字典等 10 分钟 */
  SLOWLY = 600000,
}

/** 可以缓存请求promise结果的请求方法，根据 postRequest 是否 resolve 来判断是否需要缓存 */
export function memPostRequest<T>(url: string, params = {}, config = {} as MemConfig): Promise<T> {
  const {
    maxAge = MaxAgeEnum.NORMAL,
    cacheKey = () => `${url}-${JSON.stringify(params)}`,
    forceUpdateCache = false,
    useCache = true,
    ...rest
  } = config;

  const key = cacheKey([url, params, config]);
  if (forceUpdateCache) {
    deleteCache(key);
  }

  checkMaxAge(key);

  const req = useCache ? mem(postRequest, { maxAge, cacheKey, cache: PROMISE_STORE }) : postRequest;
  return req(url, params, { ...rest, notAllowCancel: true })
    .then((res: T) => {
      // postRequest catch异常但是没有继续抛出，导致接口请求失败还是会走 then 逻辑
      // 所以需要没有 res 的时候清掉缓存
      if (!res) {
        deleteCache(cacheKey([url, params, config]));
      }

      return res;
    })
    .catch((err) => {
      deleteCache(cacheKey([url, params, config]));
      throw err;
    });
}
